import { Component } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { take } from 'rxjs';
import { CouponCodeModel } from '../../../../../models/coupon.model';
import { AlertService } from '../../../../../services/alert.service';
import { CouponService } from '../../../../../services/coupon.service';
import { ConfirmDialogComponent } from '../../../../shared-components/confirm-dialog/confirm-dialog.component';
import { CouponFormularComponent } from '../../../../shared-components/coupon-formular/coupon-formular.component';

@Component({
  selector: 'app-promo-codes',
  templateUrl: './promo-codes.component.html',
  styleUrl: './promo-codes.component.scss',
  standalone: false,
})
export class PromoCodesComponent {
  public isLoading = true;
  public promoCodes: CouponCodeModel[] = [];

  constructor(
    private couponService: CouponService,
    private dialogService: DialogService,
    private alertService: AlertService
  ) {
    this.getPromoCodes();
  }

  private getPromoCodes(): void {
    this.isLoading = true;
    this.couponService
      .getPromoCodes()
      .pipe(take(1))
      .subscribe({
        next: promoCodes => {
          this.promoCodes = promoCodes.filter(
            coupon => coupon.type === 'promo'
          );
          this.isLoading = false;
        },
        error: error => {
          console.error(error);
          this.isLoading = false;
        },
      });
  }

  public onCouponCodeActiveStateChanged(
    enabled: boolean,
    couponCodeId: number
  ): void {
    if (enabled) {
      this.enableCouponCode(couponCodeId);
    } else {
      this.disableCouponCode(couponCodeId);
    }
  }

  private enableCouponCode(couponCodeId: number): void {
    this.couponService
      .enableCouponCode(couponCodeId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.promoCodes.find(
            couponCode => couponCode.id === couponCodeId
          ).isActive = true;
          this.alertService.showSuccessAlert('Gutschein erfolgreich aktiviert');
        },
        error: error => {
          this.promoCodes.find(
            couponCode => couponCode.id === couponCodeId
          ).isActive = false;
          console.error('Failed to enable coupon code', error);
          this.alertService.showErrorAlert(
            'Beim Aktivieren des Gutscheins ist ein Fehler aufgetreten'
          );
        },
      });
  }

  private disableCouponCode(couponCodeId: number): void {
    this.couponService
      .disableCouponCode(couponCodeId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.promoCodes.find(
            couponCode => couponCode.id === couponCodeId
          ).isActive = false;
          this.alertService.showSuccessAlert(
            'Gutschein erfolgreich deaktiviert'
          );
        },
        error: error => {
          this.promoCodes.find(
            couponCode => couponCode.id === couponCodeId
          ).isActive = true;
          console.error('Failed to disable coupon code', error);
          this.alertService.showErrorAlert(
            'Beim Aktivieren des Gutscheins ist ein Fehler aufgetreten'
          );
        },
      });
  }

  public onCouponCodeDeleted(couponCode: CouponCodeModel): void {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      width: '475px',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '640px': '90vw',
      },
      data: {
        title: 'Promo Code löschen',
        message:
          'Bist du sicher, dass du den Promo Code löschen möchtest? Nach Entfernen des Promo Codes kann dieser nicht mehr verwendet werden.',
        icon: 'fa-regular fa-trash-can',
        iconColor: 'red',
        confirmButtonText: 'Löschen',
        confirmButtonIcon: 'fa-regular fa-trash-can',
        cancelButtonIcon: 'fa-regular fa-times',
      },
    });
    dialogRef.onClose.pipe(take(1)).subscribe((response: boolean) => {
      if (!response) {
        return;
      }
      this.couponService
        .deleteCouponCode(couponCode.id)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.promoCodes = this.promoCodes.filter(
              coupon => coupon.id !== couponCode.id
            );
            this.alertService.showSuccessAlert(
              'Gutschein erfolgreich gelöscht'
            );
          },
          error: error => {
            console.error('Failed to delete coupon code', error);
            this.alertService.showErrorAlert(
              'Beim Löschen des Gutscheins ist ein Fehler aufgetreten'
            );
          },
        });
    });
  }

  public addPromoCode() {
    const dialogRef = this.dialogService.open(CouponFormularComponent, {
      width: '600px',
      data: {
        type: 'promo',
      },
    });
    dialogRef.onClose.subscribe((response: boolean) => {
      if (response) {
        this.getPromoCodes();
      }
    });
  }

  public editPromoCode(couponCode: CouponCodeModel): void {
    const dialogRef = this.dialogService.open(CouponFormularComponent, {
      width: '600px',
      data: {
        type: 'promo',
        couponCodeId: couponCode.id,
      },
    });
    dialogRef.onClose.subscribe((response: boolean) => {
      if (response) {
        this.getPromoCodes();
      }
    });
  }
}
